/**
 * Domain types for the admin Profile Cleaning feature. Pure data shapes; no
 * React, Redux or fetch. These mirror the loose JSON the `/dead-profiles` and
 * `/duplicate-profiles` endpoints return (inlined in the original page) plus the
 * unified `CleaningRow` the DataTable renders.
 */

/** Initial-load lifecycle for the two lists fetched on mount. */
export type LoadStatus = 'idle' | 'loading' | 'loaded' | 'failed';

/** A profile whose LinkedIn URL is broken (returned by GET /dead-profiles). */
export interface BrokenUrlProfile {
  id: string;
  full_name: string;
  linkedin_url: string;
  status: string;
  /** Locked from cleaning (#702): listed with its padlock, never purged. */
  is_cleaning_locked: boolean;
  updated_at: string;
}

/** One member of a duplicate group. */
export interface DuplicateProfileEntry {
  id: string;
  full_name: string;
  linkedin_url: string;
  linkedin_id: string | null;
  data_updated_at: string | null;
  is_canonical: boolean;
  /** Locked from cleaning (#702): never merged away, so the backend ranks it canonical. */
  is_cleaning_locked: boolean;
}

/**
 * A set of profiles detected as duplicates of one another: their LinkedIn URLs
 * reduce to the same `/in/<slug>`, which is the same account (#657).
 */
export interface DuplicateGroup {
  /** The bare `/in/<slug>` URL every member reduces to; the group's key. */
  linkedin_url: string;
  type: 'same_linkedin_profile';
  profiles: DuplicateProfileEntry[];
  canonical_id: string;
}

/**
 * What kind of problem a cleaning row is, in the order the filter shows them.
 * More kinds are expected (#431), so this list is the single source: the union
 * below, the type filter's segments, the Type chip and the clean scopes are all
 * derived from it; adding a kind is adding a string here (plus its label and
 * its clean step).
 */
export const CLEANING_ROW_TYPES = [
  'broken_url',
  'duplicate',
  'mixed_exit_currencies',
  'exits_investments_to_complete',
] as const;

/**
 * Kinds an administrator reads and fixes elsewhere, never through a clean: a
 * profile whose exits are in several currencies (#709) is converted by hand
 * (a re-import with an overwrite). Its rows are not selectable and out of
 * every clean and of its counts.
 */
export const REVIEW_ONLY_ROW_TYPES: readonly CleaningRowType[] = [
  'mixed_exit_currencies',
  'exits_investments_to_complete',
];

/**
 * Kinds kept out of "All issues" (#754): listed only while their own card is
 * selected. A Business Angel or Exit entrepreneur whose Exits & investments tab
 * is empty is a profile to complete, not an issue to clean, and some 800 of them
 * would bury the rows the default table is there to clean. Out of the "All
 * issues" figure too, like the sidebar badge (#753), which never counts them.
 */
export const CARD_ONLY_ROW_TYPES: readonly CleaningRowType[] = ['exits_investments_to_complete'];

/**
 * A Business Angel or Exit entrepreneur whose Exits & investments tab is empty
 * and that no expert verified (returned by GET
 * /exits-investments-to-complete-profiles, #754).
 */
export type { ExitsInvestmentsToCompleteProfile } from '@shared/schemas';

/**
 * A profile whose exit amounts are not all in one currency (returned by GET
 * /mixed-currency-exit-profiles, #709). `null` in `currencies` stands for the
 * amounts given without one.
 */
export type { MixedCurrencyExitProfile } from '@shared/schemas';

export type CleaningRowType = (typeof CLEANING_ROW_TYPES)[number];

/** The Type filter's value; every kind, or exactly one of them. */
export type CleaningTypeFilter = 'all' | CleaningRowType;

/** Unified row for the cleaning DataTable (broken URL or a duplicate member). */
export interface CleaningRow {
  id: string;
  full_name: string;
  linkedin_url: string;
  row_type: CleaningRowType;
  reason: string;
  date: string;
  /** Locked from cleaning (#702): shown, not selectable, and out of every clean. */
  is_cleaning_locked: boolean;
  // Duplicate-specific fields
  /** The group's bare LinkedIn URL; which group a row belongs to. */
  group_key?: string;
  is_canonical?: boolean;
  canonical_id?: string;
  duplicate_ids?: string[];
}

// ── Gateway result shapes (inlined in the original page) ──

export interface DeleteBrokenUrlsResult {
  deleted_count: number;
  updated_pipelines: number;
  /** Scoped delete only: ids that were no longer deletable (already gone, or re-enriched). */
  skipped_count?: number;
}

export interface MergeGroupResult {
  merged: number;
  errors?: string[];
}
